import { Color, Euler, Matrix4, Quaternion, Vector2, Vector3 } from "three";

/**
 * Stable serialization and fast structural comparison for build-step dependency payloads
 * (`AbstractProduct` incremental rebuilds).
 *
 * ## Why two public entry points?
 *
 * **`dependenciesUnchanged(a, b)`** — cheap recursive equality for values captured at the
 * *same moment* (typically old vs new dependency for one build pass). Same reference or
 * deeply equal plain data → `true`. It does **not** detect in-place mutation of shared
 * objects (e.g. `vector3.set(...)` on a nested `Vector3`).
 *
 * **`stableFingerprint(value)`** — deterministic cache key for a *previous* build's deps.
 * Hashes canonical structure (FNV-1a64 → 16 hex chars). Picks up in-place mutation because
 * known instances (Vector3, Color, …) serialize current field values, not object identity.
 *
 * Callers combine both: fast-compare first, then fingerprint against stored value from last build.
 *
 * ## Fingerprint pipeline (`stableFingerprint`)
 *
 * ```
 * value ──► canonicalize (typed tags, sorted keys / Map entries / Set items)
 *       ──► FNV-1a64 UTF-8 ──► 16-char hex string
 * ```
 *
 * Cycles: `visiting` set + `cycle:iid:<n>` back-edges (per-object instance id in this process).
 *
 * ## Compare pipeline (`dependenciesUnchanged`)
 *
 * ```
 * compareDependencies ──► comparePrimitivePair (early exit for null, numbers, same ref)
 *                     ──► compareObjectPair / comparePlainObjects / compareKnownInstances
 * ```
 *
 * Cycles: `paired` WeakMap — when object `a` is seen again, the partner must still be the
 * same `b` reference recorded on first visit (isomorphic graphs with fresh objects → `true`).
 *
 * ## Identity vs value (summary)
 *
 * | Kind | Fingerprint | Fast compare |
 * |------|-------------|--------------|
 * | Plain object / array | Structural (sorted keys) | Structural |
 * | Vector2/3, Euler, Color, Matrix4, Date, RegExp | Field values | Field values |
 * | Map / Set | Structural (sorted) | Always `false` — use fingerprint |
 * | Function | `toString` + instance id | Always `false` |
 * | `Symbol.for` | Registry key | Always `false` |
 * | Local `Symbol` | Instance id + description | Always `false` |
 *
 * Add branches in `canonicalizeKnownInstance` / `compareKnownInstances` when new dependency
 * types need custom serialization (e.g. additional Three.js types).
 */

/** Field separator in canonical strings (ASCII unit separator — unlikely in user data). */
const SEP = "\x1f";

/** Per-function object identity — `toString()` omits closure captures. */
const functionInstanceIds = new WeakMap<
  (...args: unknown[]) => unknown,
  bigint
>();
let nextFunctionInstanceId = 1n;

/** Per-symbol identity — `String(sym)` collides for same description / anonymous symbols. */
const symbolInstanceIds = new Map<symbol, bigint>();
let nextSymbolInstanceId = 1n;

/** Per-object identity for cycle back-edges. */
const objectInstanceIds = new WeakMap<object, bigint>();
let nextObjectInstanceId = 1n;

/** Stable numeric id for an object reference within this process (cycle back-edges). */
function objectInstanceId(obj: object): bigint {
  let id = objectInstanceIds.get(obj);
  if (id === undefined) {
    id = nextObjectInstanceId++;
    objectInstanceIds.set(obj, id);
  }
  return id;
}

/** Canonical token when `canonicalize` revisits an object already on the `visiting` stack. */
function cycleBackEdgeMarker(obj: object): string {
  return `cycle:iid:${objectInstanceId(obj).toString()}`;
}

/** Stable numeric id for a function reference within this process (closure identity). */
function functionInstanceId(fn: (...args: unknown[]) => unknown): bigint {
  let id = functionInstanceIds.get(fn);
  if (id === undefined) {
    id = nextFunctionInstanceId++;
    functionInstanceIds.set(fn, id);
  }
  return id;
}

/** UTF-8 bytes for FNV; encoder reused to avoid allocation churn in hot path. */
const utf8Encoder = new TextEncoder();

/** Sort object keys alphabetically (ASCII) for stable fingerprints. */
function compareObjectKeys(a: string, b: string): number {
  return cmpUtf16(a, b);
}

/** Compare canonical strings by UTF-16 code units (Map/Set ordering; unchanged vs. legacy). */
function cmpUtf16(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

/** Cross-build cache key; see module doc for pipeline and identity rules. */
export function stableFingerprint(value: unknown): string {
  return fnv1a64(canonicalize(value, new Set()));
}

/**
 * Same-frame structural equality; see module doc for cycle handling and opt-out types.
 * Always pair with `stableFingerprint` for cross-build invalidation (in-place mutation).
 */
export function dependenciesUnchanged(a: unknown, b: unknown): boolean {
  return compareDependencies(a, b, new WeakMap());
}

/** Numeric equality with `NaN === NaN` semantics. */
function compareNumbers(a: number, b: number): boolean {
  if (Number.isNaN(a) || Number.isNaN(b)) {
    return Number.isNaN(a) && Number.isNaN(b);
  }
  return a === b;
}

/**
 * Fast path for primitives and same-reference objects.
 * @returns `null` when either side is a distinct object needing deep compare.
 */
function comparePrimitivePair(a: unknown, b: unknown): boolean | null {
  if (typeof a === "function" || typeof b === "function") {
    return false;
  }
  if (a === null || b === null) {
    return a === b;
  }
  if (a === undefined || b === undefined) {
    return a === b;
  }

  const ta = typeof a;
  const tb = typeof b;
  if (ta !== tb) {
    return false;
  }
  if (ta === "symbol") {
    return false;
  }
  if (ta === "number") {
    return compareNumbers(a as number, b as number);
  }
  if (ta !== "object") {
    return a === b;
  }

  if (a === b) {
    return true;
  }

  return null;
}

/**
 * Value equality for registered dependency types (Three.js, Date, RegExp, …).
 * @returns `null` when no matching known-instance pair was compared.
 */
function compareKnownInstances(ao: object, bo: object): boolean | null {
  if (ao instanceof Date && bo instanceof Date) {
    return ao.getTime() === bo.getTime();
  }
  if (ao instanceof Vector2 && bo instanceof Vector2) {
    return ao.equals(bo);
  }
  if (ao instanceof Vector3 && bo instanceof Vector3) {
    return ao.equals(bo);
  }
  if (ao instanceof Euler && bo instanceof Euler) {
    return (
      ao.x === bo.x && ao.y === bo.y && ao.z === bo.z && ao.order === bo.order
    );
  }
  if (ao instanceof Quaternion && bo instanceof Quaternion) {
    return ao.x === bo.x && ao.y === bo.y && ao.z === bo.z && ao.w === bo.w;
  }
  if (ao instanceof Matrix4 && bo instanceof Matrix4) {
    return compareMatrix4Elements(ao.elements, bo.elements);
  }
  if (ao instanceof Color && bo instanceof Color) {
    return ao.r === bo.r && ao.g === bo.g && ao.b === bo.b;
  }
  if (ao instanceof RegExp && bo instanceof RegExp) {
    return ao.source === bo.source && ao.flags === bo.flags;
  }

  return null;
}

function isKnownInstance(value: object): boolean {
  return (
    value instanceof Date ||
    value instanceof Vector2 ||
    value instanceof Vector3 ||
    value instanceof Euler ||
    value instanceof Quaternion ||
    value instanceof Matrix4 ||
    value instanceof Color ||
    value instanceof RegExp
  );
}

/** Element-wise strict equality for `Matrix4.elements` arrays. */
function compareMatrix4Elements(
  ae: ArrayLike<number>,
  be: ArrayLike<number>,
): boolean {
  if (ae.length !== be.length) {
    return false;
  }
  for (let i = 0; i < ae.length; i++) {
    if (ae[i] !== be[i]) {
      return false;
    }
  }
  return true;
}

/** Pairwise deep compare of array slots (order-sensitive). */
function compareArrays(
  ao: unknown[],
  bo: unknown[],
  paired: WeakMap<object, object>,
): boolean {
  if (ao.length !== bo.length) {
    return false;
  }
  for (let i = 0; i < ao.length; i++) {
    if (!compareDependencies(ao[i], bo[i], paired)) {
      return false;
    }
  }
  return true;
}

/**
 * Dispatch object compare by runtime type.
 * Map/Set always `false` here — callers must use `stableFingerprint`.
 */
function compareObjectPair(
  ao: object,
  bo: object,
  paired: WeakMap<object, object>,
): boolean {
  if (
    ao instanceof Map ||
    ao instanceof Set ||
    bo instanceof Map ||
    bo instanceof Set
  ) {
    return false;
  }

  const known = compareKnownInstances(ao, bo);
  if (known !== null) {
    return known;
  }
  if (isKnownInstance(ao) || isKnownInstance(bo)) {
    return false;
  }

  if (Array.isArray(ao) && Array.isArray(bo)) {
    return compareArrays(ao, bo, paired);
  }
  if (Array.isArray(ao) || Array.isArray(bo)) {
    return false;
  }

  return comparePlainObjects(ao, bo, paired);
}

/** Depth-first structural compare; `paired` maps visited `a` objects to their `b` partner for cycles. */
function compareDependencies(
  a: unknown,
  b: unknown,
  paired: WeakMap<object, object>,
): boolean {
  const primitive = comparePrimitivePair(a, b);
  if (primitive !== null) {
    return primitive;
  }

  const ao = a as object;
  const bo = b as object;
  const pairedB = paired.get(ao);
  if (pairedB !== undefined) {
    return pairedB === bo;
  }
  paired.set(ao, bo);

  try {
    return compareObjectPair(ao, bo, paired);
  } finally {
    paired.delete(ao);
  }
}

/**
 * Compare plain objects and class instances by enumerable own keys (string + symbol).
 * Non-enumerable properties are ignored, matching `Object.keys` / fingerprint behavior.
 */
function comparePlainObjects(
  ao: object,
  bo: object,
  paired: WeakMap<object, object>,
): boolean {
  const aRecord = ao as Record<string, unknown>;
  const bRecord = bo as Record<string, unknown>;

  const aKeys = Object.keys(aRecord);
  const bKeys = Object.keys(bRecord);
  if (aKeys.length !== bKeys.length) {
    return false;
  }
  aKeys.sort(compareObjectKeys);
  bKeys.sort(compareObjectKeys);
  for (let i = 0; i < aKeys.length; i++) {
    if (aKeys[i] !== bKeys[i]) {
      return false;
    }
    const key = aKeys[i];
    if (key === undefined) {
      return false;
    }
    if (!compareDependencies(aRecord[key], bRecord[key], paired)) {
      return false;
    }
  }

  const aSymbols = Object.getOwnPropertySymbols(aRecord).filter(
    (sym) => Object.getOwnPropertyDescriptor(aRecord, sym)?.enumerable === true,
  );
  const bSymbols = Object.getOwnPropertySymbols(bRecord).filter(
    (sym) => Object.getOwnPropertyDescriptor(bRecord, sym)?.enumerable === true,
  );
  if (aSymbols.length !== bSymbols.length) {
    return false;
  }
  aSymbols.sort((x, y) => cmpUtf16(String(x), String(y)));
  bSymbols.sort((x, y) => cmpUtf16(String(x), String(y)));
  for (let i = 0; i < aSymbols.length; i++) {
    if (String(aSymbols[i]) !== String(bSymbols[i])) {
      return false;
    }
    const sym = aSymbols[i];
    if (sym === undefined) {
      return false;
    }
    if (
      !compareDependencies(
        Reflect.get(aRecord, sym),
        Reflect.get(bRecord, sym),
        paired,
      )
    ) {
      return false;
    }
  }

  return true;
}

/** Canonical string for a symbol (`Symbol.for` by registry key; local symbols by instance id). */
function fingerprintSymbol(sym: symbol): string {
  const forKey = Symbol.keyFor(sym);
  if (forKey !== undefined) {
    return `sym:for:${forKey.length}${SEP}${forKey}`;
  }
  let sid = symbolInstanceIds.get(sym);
  if (sid === undefined) {
    sid = nextSymbolInstanceId++;
    symbolInstanceIds.set(sym, sid);
  }
  const description = sym.description ?? "";
  return `sym:iid:${sid.toString()}${SEP}${description.length}${SEP}${description}`;
}

/**
 * Canonical string for a function: `toString()` plus instance id.
 * Falls back to name + arity when `toString` throws (e.g. bound native functions).
 */
function fingerprintFunction(fn: (...args: unknown[]) => unknown): string {
  const iid = functionInstanceId(fn);
  const iidPart = `iid:${iid.toString()}`;
  try {
    const src = Function.prototype.toString.call(fn);
    return `fn:${iidPart}${SEP}${src.length}${SEP}${src}`;
  } catch {
    const name = fn.name;
    return `fn:${iidPart}${SEP}n${name.length}${SEP}${name}${SEP}arity:${fn.length}:fallback`;
  }
}

/** Serialize a plain object or class instance (enumerable own keys, sorted). */
function canonicalizePlainObject(value: object, visiting: Set<object>): string {
  const record = value as Record<string, unknown>;
  const stringKeys = Object.keys(record);
  stringKeys.sort(compareObjectKeys);
  const stringParts = stringKeys.map(
    (k) => `${k.length}${SEP}${k}${SEP}${canonicalize(record[k], visiting)}`,
  );

  const symbolKeys = Object.getOwnPropertySymbols(record).filter(
    (sym) => Object.getOwnPropertyDescriptor(record, sym)?.enumerable === true,
  );
  symbolKeys.sort((a, b) =>
    cmpUtf16(canonicalize(a, visiting), canonicalize(b, visiting)),
  );
  const symbolParts = symbolKeys.map(
    (sym) =>
      `${canonicalize(sym, visiting)}${SEP}${canonicalize(Reflect.get(record, sym), visiting)}`,
  );

  const inner = joinObjectParts(stringParts, symbolParts);
  return `obj:${stringKeys.length + symbolKeys.length}:${inner}`;
}

/** Join string-key and symbol-key property parts with a single separator between groups. */
function joinObjectParts(stringParts: string[], symbolParts: string[]): string {
  if (symbolParts.length === 0) {
    return stringParts.join(SEP);
  }
  if (stringParts.length === 0) {
    return symbolParts.join(SEP);
  }
  return `${stringParts.join(SEP)}${SEP}${symbolParts.join(SEP)}`;
}

/**
 * Serialize non-object primitives and functions/symbols.
 * @returns `null` for object values (handled by `canonicalizeObject`).
 */
function canonicalizePrimitive(value: unknown): string | null {
  if (value === null) {
    return "null";
  }
  if (value === undefined) {
    return "undef";
  }
  const t = typeof value;
  if (t === "boolean") {
    return value ? "bool:1" : "bool:0";
  }
  if (t === "number") {
    const n = value as number;
    if (Number.isNaN(n)) {
      return "num:nan";
    }
    return `num:${n.toString()}`;
  }
  if (t === "string") {
    const s = value as string;
    return `str:${s.length}${SEP}${s}`;
  }
  if (t === "bigint") {
    const bi = value as bigint;
    return `bigint:${bi.toString()}`;
  }
  if (t === "symbol") {
    return fingerprintSymbol(value as symbol);
  }
  if (t === "function") {
    const fn = value as (...args: unknown[]) => unknown;
    return fingerprintFunction(fn);
  }

  return null;
}

/**
 * Typed serialization for registered dependency instances.
 * @returns `null` to fall through to array / Map / Set / plain-object handling.
 */
function canonicalizeKnownInstance(value: object): string | null {
  if (value instanceof Date) {
    return `date:${value.getTime()}`;
  }
  if (value instanceof Vector2) {
    return `v2:${value.x},${value.y}`;
  }
  if (value instanceof Vector3) {
    return `v3:${value.x},${value.y},${value.z}`;
  }
  if (value instanceof Euler) {
    return `euler:${value.x},${value.y},${value.z},${value.order}`;
  }
  if (value instanceof Quaternion) {
    return `quat:${value.x},${value.y},${value.z},${value.w}`;
  }
  if (value instanceof Matrix4) {
    const elements = Array.from(value.elements, (n) => n.toString());
    return `m4:${elements.join(",")}`;
  }
  if (value instanceof Color) {
    return `col:${value.r},${value.g},${value.b}`;
  }
  if (value instanceof RegExp) {
    return `re:${value.flags}${SEP}${value.source}`;
  }

  return null;
}

/** Serialize array elements in index order (length prefix + joined items). */
function canonicalizeArray(value: unknown[], visiting: Set<object>): string {
  const parts = value.map((item) => canonicalize(item, visiting));
  return `arr:${value.length}:${parts.join(SEP)}`;
}

/** Serialize Map entries sorted by canonical key string (insertion order ignored). */
function canonicalizeMap(
  value: Map<unknown, unknown>,
  visiting: Set<object>,
): string {
  const entries = Array.from(value.entries()).map(([key, entryValue]) => ({
    key: canonicalize(key, visiting),
    value: canonicalize(entryValue, visiting),
  }));
  entries.sort((a, b) => cmpUtf16(a.key, b.key));
  const inner = entries.map((entry) => `${entry.key}=${entry.value}`).join(SEP);
  return `map:${value.size}:${inner}`;
}

/** Serialize Set members sorted by canonical item string (insertion order ignored). */
function canonicalizeSet(value: Set<unknown>, visiting: Set<object>): string {
  const items = Array.from(value.values()).map((v) =>
    canonicalize(v, visiting),
  );
  items.sort(cmpUtf16);
  return `set:${value.size}:${items.join(SEP)}`;
}

/** Pick the appropriate object serializer (known instance, array, Map, Set, or plain object). */
function canonicalizeObject(value: object, visiting: Set<object>): string {
  const knownInstance = canonicalizeKnownInstance(value);
  if (knownInstance !== null) {
    return knownInstance;
  }
  if (Array.isArray(value)) {
    return canonicalizeArray(value, visiting);
  }
  if (value instanceof Map) {
    return canonicalizeMap(value, visiting);
  }
  if (value instanceof Set) {
    return canonicalizeSet(value, visiting);
  }

  return canonicalizePlainObject(value, visiting);
}

/** Serialize `value` to a deterministic string before hashing; `visiting` detects cycles. */
function canonicalize(value: unknown, visiting: Set<object>): string {
  const primitive = canonicalizePrimitive(value);
  if (primitive !== null) {
    return primitive;
  }

  const obj = value as object;
  if (visiting.has(obj)) {
    return cycleBackEdgeMarker(obj);
  }
  visiting.add(obj);
  try {
    return canonicalizeObject(obj, visiting);
  } finally {
    visiting.delete(obj);
  }
}

/** FNV-1a 64-bit → 16 hex chars (compact cache keys). */
function fnv1a64(input: string): string {
  const utf8 = utf8Encoder.encode(input);
  let hash = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  for (const byte of utf8) {
    hash ^= BigInt(byte);
    hash = (hash * prime) & 0xffffffffffffffffn;
  }
  return hash.toString(16).padStart(16, "0");
}
